import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAudio } from "./AudioContext";

const TEAL   = "#4DB8B5";
const BG     = "rgba(255,255,255,0.97)";
const BORDER = "rgba(0,0,0,0.08)";

const FIDGETS = [
  { id: "bubbles", label: "Bubbles", icon: "◉" },
  { id: "spinner", label: "Spinner", icon: "✦" },
  { id: "clicks",  label: "Clicker", icon: "◈" },
  { id: "zen",     label: "Rake",    icon: "〰" },
];

// ── Shared AudioContext singleton — fixes browser suspend after inactivity ──
let _audioCtx = null;
let isAudioMuted = false;
function getCtx() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (_audioCtx.state === "suspended") _audioCtx.resume();
    return _audioCtx;
  } catch(e) { return null; }
}

// ── Audio helpers ───────────────────────────────────────────────
const NOTES = [261.6, 293.7, 329.6, 392.0, 440.0, 523.3, 587.3, 659.3];

function playBubblePop(col, row, popping) {
  if (isAudioMuted) return;
  const ctx = getCtx(); if (!ctx) return;
  const base = NOTES[col % NOTES.length];
  const freq = popping ? base * (1 + row * 0.04) : base * 0.6;
  const osc  = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq * 1.4, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(freq * 0.5, ctx.currentTime + 0.07);
  gain.gain.setValueAtTime(popping ? 0.2 : 0.1, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
  osc.start(); osc.stop(ctx.currentTime + 0.1);
}

function playClick() {
  if (isAudioMuted) return;
  const ctx = getCtx(); if (!ctx) return;
  const buf  = ctx.createBuffer(1, ctx.sampleRate * 0.04, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random()*2-1) * Math.exp(-i/120);
  const src  = ctx.createBufferSource();
  const gain = ctx.createGain();
  src.buffer = buf; src.connect(gain); gain.connect(ctx.destination);
  gain.gain.value = 0.35;
  src.start();
}

function playWhiz(speed) {
  if (isAudioMuted) return;
  const ctx = getCtx(); if (!ctx) return;
  const osc  = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(Math.min(900, 80 + speed * 18), ctx.currentTime);
  gain.gain.setValueAtTime(0.04, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
  osc.start(); osc.stop(ctx.currentTime + 0.12);
}

// ── Bubble Wrap ─────────────────────────────────────────────────
const BUBBLE_COLORS = [
  { h: 178, name: "teal"     },
  { h: 260, name: "lavender" },
  { h: 20,  name: "peach"    },
  { h: 145, name: "mint"     },
  { h: 330, name: "pink"     },
  { h: 42,  name: "gold"     },
];

function BubbleWrap() {
  const ROWS = 6, COLS = 8, total = ROWS * COLS;
  const [states,   setStates]   = useState(() => Array(total).fill(null));
  const [count,    setCount]    = useState(0);
  const [colorIdx, setColorIdx] = useState(0);
  const mouseDown = useRef(false);

  const { h } = BUBBLE_COLORS[colorIdx];
  const bubbleBg     = `radial-gradient(circle at 35% 35%, hsl(${h},100%,98%) 0%, hsl(${h},60%,82%) 40%, hsl(${h},55%,72%) 100%)`;
  const bubbleBorder = `hsl(${h},50%,65%)`;
  const bubbleShadow = `0 2px 5px hsla(${h},60%,50%,0.25), inset 0 1px 0 rgba(255,255,255,0.85)`;

  // Only pops unpopped bubbles - no re-inflate on drag (only click)
  const tryPop = (i) => {
    const row = Math.floor(i / COLS), col = i % COLS;
    setStates(prev => {
      const s = prev[i];
      if (s !== null) return prev; // already popped/animating
      playBubblePop(col, row, true);
      const n = [...prev]; n[i] = "popping";
      setTimeout(() => setStates(p => { const nn=[...p]; nn[i]="popped"; return nn; }), 130);
      return n;
    });
    setCount(c => c + 1);
  };

  const toggle = (i) => {
    const row = Math.floor(i / COLS), col = i % COLS;
    const s = states[i];
    if (s === "popping" || s === "inflating") return;
    if (!s) {
      tryPop(i);
    } else if (s === "popped") {
      // Re-inflate only on direct click, not drag
      playBubblePop(col, row, false);
      setCount(c => c - 1);
      setStates(p => { const n=[...p]; n[i]="inflating"; return n; });
      setTimeout(() => setStates(p => { const n=[...p]; n[i]=null; return n; }), 200);
    }
  };

  const reset = () => {
    setStates(s => s.map(v => v === "popped" ? "inflating" : v));
    setTimeout(() => { setStates(Array(total).fill(null)); setCount(0); }, 250);
  };

  return (
    <div className="flex flex-col items-center gap-2.5 py-2">
      <div className="flex gap-1.5">
        {BUBBLE_COLORS.map((c, i) => (
          <div key={c.name} onClick={() => setColorIdx(i)}
            className="w-4 h-4 rounded-full cursor-pointer transition-all duration-150"
            style={{
              background: `hsl(${c.h},60%,72%)`,
              boxShadow: i === colorIdx ? `0 0 0 2px white, 0 0 0 3.5px hsl(${c.h},60%,55%)` : "none",
              border: i === colorIdx ? `2px solid hsl(${c.h},60%,45%)` : "2px solid transparent"
            }}/>
        ))}
      </div>
      <div
        onMouseLeave={() => { mouseDown.current = false; }}
        onMouseUp={() => { mouseDown.current = false; }}
        className="grid gap-[5px] select-none"
        style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}>
        {Array.from({length: total}, (_,i) => {
          const s = states[i];
          return (
            <div key={i}
              onMouseDown={() => { mouseDown.current = true; toggle(i); }}
              onMouseEnter={() => { if (mouseDown.current) toggle(i); }}
              onMouseUp={() => { mouseDown.current = false; }}
              className="w-[26px] h-[26px] rounded-full"
              style={{
                cursor: (s==="popping"||s==="inflating") ? "default" : "pointer",
                transition: s==="inflating" ? "all 0.2s ease-out" : "all 0.13s ease-in",
                background: s==="popped" ? "rgba(0,0,0,0.04)" : bubbleBg,
                border: s==="popped" ? "1px solid rgba(0,0,0,0.06)" : `1px solid ${bubbleBorder}`,
                boxShadow: s==="popped" ? "none" : bubbleShadow,
                transform: s==="popping" ? "scale(1.28)" : s==="popped" ? "scale(0.7)" : s==="inflating" ? "scale(1.08)" : "scale(1)",
              }}/>
          );
        })}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-slate-400 font-['DM_Sans']">{count}/{total} popped</span>
        {count > 0 && (
          <button onClick={reset} className="text-[11px] py-[3px] px-2.5 rounded-[10px] cursor-pointer font-['DM_Sans']"
            style={{ border: `1px solid hsla(${h},50%,60%,0.4)`, background: `hsla(${h},60%,95%,1)`, color: `hsl(${h},50%,45%)` }}>
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

// ── Spinner ─────────────────────────────────────────────────────
// Rainbow color based on speed: violet → blue → teal → green → yellow → orange → red
function speedColor(speed) {
  const t = Math.min(1, speed / 30);
  const hue = 270 - t * 270; // 270=violet → 0=red
  return `hsl(${hue}, 90%, 55%)`;
}

function Spinner() {
  const [angle,    setAngle]    = useState(0);
  const [velocity, setVelocity] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [holding,  setHolding]  = useState(false);
  const lastAngle = useRef(null);
  const lastTime  = useRef(null);
  const frameRef  = useRef(null);
  const centerRef = useRef({ x:0, y:0 });
  const svgRef    = useRef(null);
  const holdRef   = useRef(false);
  const velRef    = useRef(0);

  useEffect(() => { velRef.current = velocity; }, [velocity]);

  useEffect(() => {
    const tick = () => {
      if (!dragging) {
        if (holdRef.current) {
          velRef.current = Math.min(velRef.current + 0.9, 38);
          setVelocity(velRef.current);
          setAngle(a => a + velRef.current);
          if (Math.random() < 0.1) playWhiz(velRef.current);
        } else {
          velRef.current *= 0.975;
          setVelocity(velRef.current);
          setAngle(a => a + velRef.current);
          if (Math.abs(velRef.current) > 4 && Math.random() < 0.05) playWhiz(Math.abs(velRef.current));
        }
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [dragging]);

  const getAngle = (e) => {
    const c  = centerRef.current;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return Math.atan2(cy - c.y, cx - c.x) * 180 / Math.PI;
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    centerRef.current = { x: rect.left + rect.width/2, y: rect.top + rect.height/2 };
    setDragging(true);
    lastAngle.current = getAngle(e);
    lastTime.current  = Date.now();
  };

  const onMouseMove = useCallback((e) => {
    if (!dragging) return;
    const a  = getAngle(e);
    const dt = Date.now() - lastTime.current;
    const diff = a - lastAngle.current;
    const adj = diff > 180 ? diff - 360 : diff < -180 ? diff + 360 : diff;
    const vel = dt > 0 ? adj / dt * 16 : 0;
    setAngle(prev => prev + adj);
    setVelocity(vel); velRef.current = vel;
    if (Math.abs(vel) > 6) playWhiz(Math.abs(vel));
    lastAngle.current = a;
    lastTime.current  = Date.now();
  }, [dragging]);

  const onMouseUp = useCallback(() => setDragging(false), []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
    return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
  }, [onMouseMove, onMouseUp]);

  const speed = Math.abs(velocity);
  const color = speedColor(speed);
  const dots  = Math.min(8, Math.round(speed / 1.5));
  const glowing = speed > 8;

  return (
    <div className="flex flex-col items-center gap-2.5 py-2">
      <svg ref={svgRef} width="160" height="160" viewBox="0 0 180 180"
        onMouseDown={onMouseDown}
        className="select-none transition-[filter] duration-300"
        style={{ cursor: dragging ? "grabbing" : "grab", filter: glowing ? `drop-shadow(0 0 14px ${color})` : "none" }}>
        <circle cx="90" cy="90" r="82" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="2"/>
        {Array.from({length:8}, (_,i) => {
          const a = (i/8)*Math.PI*2;
          const dotColor = i < dots ? speedColor(speed * (i+1)/dots) : "rgba(0,0,0,0.08)";
          return <circle key={i} cx={90+75*Math.cos(a)} cy={90+75*Math.sin(a)} r="3.5"
            fill={dotColor} style={{ transition:"fill 0.08s" }}/>;
        })}
        <g transform={`rotate(${angle} 90 90)`}>
          {[0,120,240].map((a,i) => (
            <g key={i} transform={`rotate(${a} 90 90)`}>
              <ellipse cx="90" cy="38" rx="22" ry="14" fill={color} opacity={0.9-i*0.12} style={{ transition:"fill 0.1s" }}/>
              <ellipse cx="86" cy="34" rx="8"  ry="4"  fill="rgba(255,255,255,0.4)" opacity={0.9-i*0.12}/>
            </g>
          ))}
          <circle cx="90" cy="90" r="15" fill="white" stroke={color} strokeWidth="2.5" style={{ transition:"stroke 0.1s" }}/>
          <circle cx="90" cy="90" r="6"  fill={color} opacity="0.8" style={{ transition:"fill 0.1s" }}/>
        </g>
      </svg>

      <button
        onMouseDown={() => { holdRef.current = true;  setHolding(true);  }}
        onMouseUp={()   => { holdRef.current = false; setHolding(false); }}
        onMouseLeave={() => { holdRef.current = false; setHolding(false); }}
        onTouchStart={() => { holdRef.current = true;  setHolding(true);  }}
        onTouchEnd={()   => { holdRef.current = false; setHolding(false); }}
        className="py-[7px] px-7 rounded-[10px] text-[13px] font-bold cursor-pointer font-['DM_Sans'] select-none transition-all duration-100"
        style={{
          border: `2px solid ${color}88`,
          background: holding ? `${color}22` : `${color}0D`,
          color,
          transform: holding ? "scale(0.95)" : "scale(1)",
          boxShadow: holding ? `0 0 18px ${color}66` : "none"
        }}>
        {holding ? "Spinning..." : "Hold to Spin"}
      </button>
      <div className="text-[10px] text-slate-300 font-['DM_Sans']">or drag for control</div>
      <div className="text-[11px] font-semibold font-['DM_Sans'] transition-[color] duration-100" style={{ color }}>
        {speed > 25 ? "🔥 Blazing!" : speed > 15 ? "⚡ Fast!" : speed > 5 ? "Spinning..." : "Ready"}
      </div>
    </div>
  );
}

// ── Clicker ─────────────────────────────────────────────────────
function Clicker() {
  const [count,    setCount]    = useState(0);
  const [pressing, setPressing] = useState(false);
  const [flashes,  setFlashes]  = useState([]);
  const containerRef = useRef(null);
  const btnRef       = useRef(null);

  // Auto-focus the container so keyboard works immediately
  useEffect(() => { containerRef.current?.focus(); }, []);

  const doClick = useCallback((x, y) => {
    setCount(c => c + 1);
    playClick();
    const id   = Date.now() + Math.random();
    const rect = btnRef.current?.getBoundingClientRect();
    setFlashes(f => [...f, { id, x: x ?? (rect ? rect.width/2 : 70), y: y ?? (rect ? rect.height/2 : 70) }]);
    setTimeout(() => setFlashes(f => f.filter(fl => fl.id !== id)), 600);
  }, []);

  const onKeyDown = (e) => {
    // Any key click - space, enter, or any letter key
    if (e.code === "Space" || e.code === "Enter" || e.key.length === 1) {
      e.preventDefault();
      setPressing(true);
      doClick();
      setTimeout(() => setPressing(false), 80);
    }
  };

  const milestones = [10, 25, 50, 100, 250, 500, 1000];
  const next = milestones.find(m => m > count) || count + 1000;
  const pct  = count / next;

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="flex flex-col items-center gap-3.5 py-2 outline-none">
      <div ref={btnRef} className="relative">
        <div
          onMouseDown={() => { setPressing(true); containerRef.current?.focus(); }}
          onMouseUp={(e) => { setPressing(false); const r = e.currentTarget.getBoundingClientRect(); doClick(e.clientX-r.left, e.clientY-r.top); }}
          onMouseLeave={() => setPressing(false)}
          className="w-[140px] h-[140px] rounded-full cursor-pointer select-none relative overflow-hidden flex flex-col items-center justify-center transition-all duration-[80ms]"
          style={{
            background: pressing
              ? `radial-gradient(circle, rgba(77,184,181,0.3) 0%, rgba(77,184,181,0.1) 100%)`
              : `radial-gradient(circle, rgba(77,184,181,0.15) 0%, rgba(77,184,181,0.04) 100%)`,
            border: `2.5px solid rgba(77,184,181,${pressing?0.7:0.3})`,
            boxShadow: pressing
              ? `0 0 28px rgba(77,184,181,0.3), inset 0 4px 12px rgba(0,0,0,0.06)`
              : `0 4px 20px rgba(77,184,181,0.12), inset 0 -4px 12px rgba(0,0,0,0.04)`,
            transform: pressing ? "scale(0.94)" : "scale(1)"
          }}>
          <div className="text-4xl font-extrabold text-slate-800 leading-none font-['DM_Sans']">{count}</div>
          <div className="text-[11px] text-slate-400 font-['DM_Sans'] mt-1">clicks</div>
          {flashes.map(f => (
            <div key={f.id} className="absolute w-2 h-2 rounded-full bg-[#4DB8B5]/50 animate-[ripple_0.6s_ease-out_forwards] pointer-events-none"
              style={{ left: f.x-4, top: f.y-4 }}/>
          ))}
        </div>
      </div>
      <div className="w-full max-w-[180px]">
        <div className="flex justify-between mb-1">
          <span className="text-[10px] text-slate-400 font-['DM_Sans']">Next: {next}</span>
          <span className="text-[10px] text-slate-400 font-['DM_Sans']">{Math.round(pct*100)}%</span>
        </div>
        <div className="h-[3px] bg-black/[0.06] rounded-sm">
          <div className="h-full rounded-sm bg-[#4DB8B5] transition-[width] duration-200" style={{ width: `${pct*100}%` }}/>
        </div>
      </div>
      <div className="text-[10px] text-slate-300 font-['DM_Sans']">Any key also works</div>
      <button onClick={() => setCount(0)}
        className="text-[11px] text-slate-400 bg-transparent border-none cursor-pointer font-['DM_Sans']">
        Reset
      </button>
    </div>
  );
}

// ── Zen Rake ────────────────────────────────────────────────────
const STONE_INIT = [
  { x: 55,  y: 70,  r: 11, color: "#8A9BAD" },
  { x: 148, y: 52,  r: 8,  color: "#7A8D9E" },
  { x: 95,  y: 145, r: 13, color: "#95A5B5" },
  { x: 162, y: 138, r: 7,  color: "#6E8090" },
];
const TINES = 5, TINE_GAP = 5;

function ZenRake() {
  const canvasRef  = useRef(null);
  const stonesRef  = useRef(STONE_INIT.map(s => ({...s})));
  const strokesRef = useRef([]); // all rake strokes: [{from, to}]
  const drawing    = useRef(false);
  const lastPos    = useRef(null);
  const dragStone  = useRef(null);
  const sandRef    = useRef(null); // offscreen canvas for sand texture
  const [, rerender] = useState(0);

  // Draw sand texture once into an offscreen canvas
  useEffect(() => {
    const off = document.createElement("canvas");
    off.width = 200; off.height = 200;
    const ctx = off.getContext("2d");
    ctx.fillStyle = "#F2EBD9";
    ctx.fillRect(0, 0, 200, 200);
    for (let i = 0; i < 900; i++) {
      ctx.fillStyle = `rgba(${155+Math.random()*30},${130+Math.random()*20},${90+Math.random()*20},0.13)`;
      ctx.fillRect(Math.random()*200, Math.random()*200, 1.5, 1);
    }
    sandRef.current = off;
  }, []);

  const drawStones = useCallback((ctx) => {
    stonesRef.current.forEach(s => {
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.22)"; ctx.shadowBlur = 7;
      ctx.shadowOffsetX = 2; ctx.shadowOffsetY = 3;
      const g = ctx.createRadialGradient(s.x-s.r*0.3, s.y-s.r*0.35, s.r*0.05, s.x, s.y, s.r);
      g.addColorStop(0, "#CDD8E2"); g.addColorStop(1, s.color);
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI*2);
      ctx.fillStyle = g; ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.beginPath(); ctx.arc(s.x-s.r*0.28, s.y-s.r*0.3, s.r*0.28, 0, Math.PI*2);
      ctx.fillStyle = "rgba(255,255,255,0.28)"; ctx.fill();
      ctx.restore();
    });
  }, []);

  const drawStroke = useCallback((ctx, from, to) => {
    const dx = to.x - from.x, dy = to.y - from.y;
    const len = Math.hypot(dx, dy); if (len < 1) return;
    const nx = -dy/len, ny = dx/len;

    // Clip around stones
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, 200, 200);
    stonesRef.current.forEach(s => {
      ctx.moveTo(s.x + s.r + 4, s.y);
      ctx.arc(s.x, s.y, s.r + 4, 0, Math.PI*2, true);
    });
    ctx.clip("evenodd");

    for (let t = 0; t < TINES; t++) {
      const off = (t - (TINES-1)/2) * TINE_GAP;
      ctx.beginPath();
      ctx.moveTo(from.x + nx*off, from.y + ny*off);
      ctx.lineTo(to.x   + nx*off, to.y   + ny*off);
      ctx.strokeStyle = "rgba(100,72,42,0.42)";
      ctx.lineWidth = 1.5; ctx.lineCap = "round"; ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(from.x + nx*(off+2), from.y + ny*(off+2));
      ctx.lineTo(to.x   + nx*(off+2), to.y   + ny*(off+2));
      ctx.strokeStyle = "rgba(235,210,155,0.3)";
      ctx.lineWidth = 1; ctx.stroke();
    }
    ctx.restore();
  }, []);

  // Full repaint from scratch: sand + all strokes + stones
  const fullRepaint = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (sandRef.current) ctx.drawImage(sandRef.current, 0, 0);
    else { ctx.fillStyle = "#F2EBD9"; ctx.fillRect(0,0,200,200); }
    strokesRef.current.forEach(({from, to}) => drawStroke(ctx, from, to));
    drawStones(ctx);
  }, [drawStroke, drawStones]);

  useEffect(() => { fullRepaint(); }, [fullRepaint]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const hitStone = (x, y) => stonesRef.current.findIndex(s => Math.hypot(x-s.x, y-s.y) < s.r + 5);

  const onDown = (e) => {
    const p = getPos(e);
    const si = hitStone(p.x, p.y);
    if (si !== -1) { dragStone.current = si; }
    else { drawing.current = true; lastPos.current = p; }
  };

  const onMove = (e) => {
    const p = getPos(e);
    if (dragStone.current !== null) {
      const s = stonesRef.current[dragStone.current];
      s.x = Math.max(s.r+2, Math.min(198-s.r, p.x));
      s.y = Math.max(s.r+2, Math.min(198-s.r, p.y));
      fullRepaint(); // full repaint = no streaks
    } else if (drawing.current && lastPos.current) {
      const stroke = { from: lastPos.current, to: p };
      strokesRef.current.push(stroke);
      // Incremental draw (fast path - just add new stroke + redraw stones)
      const ctx = canvasRef.current.getContext("2d");
      drawStroke(ctx, stroke.from, stroke.to);
      drawStones(ctx);
      lastPos.current = p;
    }
  };

  const onUp = () => { dragStone.current = null; drawing.current = false; lastPos.current = null; };

  const clear = () => {
    strokesRef.current = [];
    stonesRef.current  = STONE_INIT.map(s => ({...s}));
    fullRepaint();
    rerender(n => n+1);
  };

  return (
    <div className="flex flex-col items-center gap-2.5 py-2">
      <canvas ref={canvasRef} width={200} height={200}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
        className="rounded-xl cursor-crosshair border border-[rgba(180,160,120,0.3)] shadow-[inset_0_2px_8px_rgba(0,0,0,0.06)] touch-none"/>
      <div className="flex items-center gap-3">
        <span className="text-[11px] text-slate-400 font-['DM_Sans']">Rake · drag stones</span>
        <button onClick={clear}
          className="text-[11px] py-[3px] px-2.5 rounded-[10px] border border-black/10 bg-white text-slate-500 cursor-pointer font-['DM_Sans']">
          Clear
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN FIDGET WIDGET
// ═══════════════════════════════════════════════════════════════
export default function FidgetWidget({ isOpen, onClose }) {
  const [fidget,   setFidget]   = useState("bubbles");
  const [pos,      setPos]      = useState({ x: window.innerWidth - 290, y: 80 });
  const [dragging, setDragging] = useState(null);
  const widgetRef = useRef(null);
  const { allMuted } = useAudio();
  useEffect(() => {
    isAudioMuted = allMuted;
  }, [allMuted]);


  const onDragStart = (e) => {
    if (e.target.closest("button") || e.target.closest("canvas") || e.target.closest("svg")) return;
    setDragging({ startX: e.clientX - pos.x, startY: e.clientY - pos.y });
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => setPos({ x: e.clientX - dragging.startX, y: e.clientY - dragging.startY });
    const up   = (e) => {
      if ((e.clientY - dragging.startY) < 60) onClose(); // dragged to top = dock
      setDragging(null);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup",   up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [dragging, onClose]);

  if (!isOpen) return null;

  const renderFidget = () => {
    switch(fidget) {
      case "bubbles": return <BubbleWrap/>;
      case "spinner": return <Spinner/>;
      case "clicks":  return <Clicker/>;
      case "zen":     return <ZenRake/>;
      default:        return <BubbleWrap/>;
    }
  };

  return (
    <div ref={widgetRef}
      className="fixed z-[200] w-[268px] rounded-2xl overflow-hidden font-['DM_Sans'] shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.06)]"
      style={{ left: pos.x, top: pos.y, background: BG, userSelect: dragging ? "none" : "auto" }}>

      <div onMouseDown={onDragStart}
        className="flex items-center justify-between px-3.5 py-2.5 cursor-grab bg-white"
        style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div className="flex items-center gap-2">
          <span className="text-[13px]">◉</span>
          <span className="text-[13px] font-bold text-slate-800">Fidget Widget</span>
        </div>
        <button onClick={onClose}
          className="bg-black/[0.04] border-none text-slate-400 rounded-md w-6 h-6 cursor-pointer text-[13px]">✕</button>
      </div>

      <div className="flex bg-white" style={{ borderBottom: `1px solid ${BORDER}` }}>
        {FIDGETS.map(f => (
          <button key={f.id} onClick={() => setFidget(f.id)}
            className="flex-1 py-2 px-1 border-none cursor-pointer font-['DM_Sans'] flex flex-col items-center gap-0.5 transition-all duration-150"
            style={{
              background: fidget===f.id ? "rgba(77,184,181,0.06)" : "transparent",
              borderBottom: fidget===f.id ? `2px solid ${TEAL}` : "2px solid transparent",
              color: fidget===f.id ? TEAL : "#94A3B8",
              fontSize: 10,
              fontWeight: fidget===f.id ? 600 : 400
            }}>
            <span className="text-[13px]">{f.icon}</span>
            <span>{f.label}</span>
          </button>
        ))}
      </div>

      <div className="px-3.5 pt-2 pb-3.5 bg-[#FAFBFC]">
        {renderFidget()}
      </div>

      <style>{`
        @keyframes ripple {
          0%   { transform:scale(1); opacity:0.7; }
          100% { transform:scale(9); opacity:0; }
        }
      `}</style>
    </div>
  );
}
